import { VIEWED_AD_TTL_MS } from './useViewedAds.constants';
import { ViewedAdsMap } from './useViewedAds.types';
import { STORAGE_KEYS } from '@/constants/storageKeys';

const getStorage = () => {
  return typeof window !== 'undefined' ? window.localStorage : null;
};

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

const isValidViewedTimestamp = (ts: unknown): ts is number =>
  typeof ts === 'number' && Number.isFinite(ts) && ts > 0;

const isViewedStillActive = (ts: unknown, now: number): ts is number =>
  isValidViewedTimestamp(ts) && now - ts < VIEWED_AD_TTL_MS;

const readMap = (): ViewedAdsMap => {
  try {
    const storage = getStorage();

    if (!storage) return {};

    const value = storage.getItem(STORAGE_KEYS.VIEWED_ADS);

    if (!value) return {};

    const parsed: unknown = JSON.parse(value);

    if (!isPlainObject(parsed)) return {};

    const result: ViewedAdsMap = {};
    Object.entries(parsed).forEach(([adId, ts]) => {
      if (isValidViewedTimestamp(ts)) {
        result[adId] = ts;
      }
    });

    return result;
  } catch {
    return {};
  }
};

const writeMap = (map: ViewedAdsMap) => {
  try {
    getStorage()?.setItem(STORAGE_KEYS.VIEWED_ADS, JSON.stringify(map));
  } catch {
    // localStorage can be blocked or quota-exhausted; fail silently
  }
};

export const getCleanViewedAdsMap = (now = Date.now()): ViewedAdsMap => {
  const map = readMap();
  let changed = false;

  Object.entries(map).forEach(([adId, ts]) => {
    if (isViewedStillActive(ts, now)) {
      return;
    }

    delete map[adId];
    changed = true;
  });

  if (changed) writeMap(map);

  return map;
};

export const markAdAsViewed = (adId?: string | number | null) => {
  if (adId == null) return;

  const cleanedMap = getCleanViewedAdsMap();
  const key = String(adId);
  const now = Date.now();
  const existingTs = cleanedMap[key];

  if (isViewedStillActive(existingTs, now)) {
    return;
  }

  cleanedMap[key] = now;
  writeMap(cleanedMap);
};

export const isAdViewed = (adId?: string | number | null) => {
  if (adId == null) return false;

  const cleanedMap = getCleanViewedAdsMap();
  const key = String(adId);
  return Boolean(cleanedMap[key]);
};
